import React, { useState, useEffect } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import {
  Menu,
  Bell,
  User,
  BookOpen,
  GraduationCap,
  FileText,
  HelpCircle,
  ChevronDown,
  Shield,
  CheckCircle,
  AlertCircle,
  Settings,
  Clock,
  Mail,
  LogOut
} from 'lucide-react';
import markoLogo from 'url:../../../assets/NewMarkologo.png';
import { Button } from '../../../common';
import { Link } from 'react-router-dom';
import { LanguageSelector } from './LanguageSelector';
import { useNotifications, useUnreadCount, useMarkAsRead, useMarkAllAsRead } from '../../../hooks/queries/useNotifications';
import { userActions } from '../../../services/telemetry';

const MARKOTM_ORG_ID = process.env.REACT_APP_MARKOTM_ORG_ID;

const DashboardHeader = ({ userName, onMenuClick }) => {
  const [openMenu, setOpenMenu] = useState(null);
  const { isSignedIn, user } = useUser();
  const { signOut } = useClerk();
  const [hasAdminAccess, setHasAdminAccess] = useState(false);

  const handleSignOut = async () => {
    setOpenMenu(null);
    userActions.signOut();
    await signOut({ redirectUrl: '/sign-in' });
  };

  // Real notifications from API
  const { data: notificationsData } = useNotifications({ limit: 10 });
  const { data: unreadCountData } = useUnreadCount();
  const markAsReadMutation = useMarkAsRead();
  const markAllAsReadMutation = useMarkAllAsRead();

  const notifications = notificationsData?.notifications || [];
  const hasUnreadNotifications = (unreadCountData?.count || 0) > 0;
  
  // Check admin access
  useEffect(() => {
    const checkAdminAccess = async () => {
      if (user && user.organizationMemberships) {
        try {
          // Find MarkoTM organization by ID (from env var)
          const markoTMOrg = user.organizationMemberships.find(membership =>
            membership.organization.id === MARKOTM_ORG_ID
          );
          const isMarkoTMAdmin = markoTMOrg?.role === 'org:admin';
          

          
          // STRICT: Only show admin button if user is admin in MarkoTM organization
          setHasAdminAccess(isMarkoTMAdmin);
        } catch (error) {
          console.error('Error checking admin access:', error);
          setHasAdminAccess(false);
        }
      } else {
        console.log('⏳ Admin Access: Waiting for user/org to load', { user: !!user, hasOrgMemberships: !!user?.organizationMemberships });
      }
    };
    
    checkAdminAccess();
  }, [user]); // Removed redundant user?.organizationMemberships to prevent excessive re-renders

  // Academy resources dropdown items - links to marketing site
  const MARKETING_URL = 'https://www.markotm.com';
  const academyResources = [
    {
      title: 'Trademark Academy',
      description: 'Learn about trademark fundamentals and strategies',
      icon: GraduationCap,
      link: `${MARKETING_URL}/resources/academy`
    },
    {
      title: 'Country Guides',
      description: 'Specific trademark requirements by country',
      icon: BookOpen,
      link: `${MARKETING_URL}/resources/country-guide`
    },
    {
      title: 'Trademark Dictionary',
      description: 'Definitions of common trademark terms',
      icon: FileText,
      link: `${MARKETING_URL}/resources/dictionary`
    },
    {
      title: 'Help Center',
      description: 'Get assistance with your application',
      icon: HelpCircle,
      link: `${MARKETING_URL}/resources/help`
    }
  ];

  // Helper to format notification time
  const formatNotificationTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  // Map notification urgency/category to type for icon styling
  const getNotificationType = (notification) => {
    if (notification.urgency === 'critical' || notification.urgency === 'high') return 'warning';
    if (notification.category === 'office_action' || notification.category === 'deadline') return 'warning';
    if (notification.category === 'status_change' || notification.category === 'filing_update') return 'success';
    if (notification.category === 'correspondence') return 'info';
    return 'info';
  };

  // Settings menu items
  const settingsItems = [
    {
      title: 'Account Settings',
      description: 'Update your profile and preferences',
      icon: User,
      action: () => window.location.hash = 'settings'
    },
    {
      title: 'Notification Preferences',
      description: 'Control what updates you receive',
      icon: Bell,
      action: () => window.location.hash = 'settings'
    },
    {
      title: 'Billing & Payments',
      description: 'Manage payment methods and invoices',
      icon: FileText,
      action: () => window.location.hash = 'settings'
    },
  ];

  // Dropdown menu components
  const ResourceDropdown = ({ isOpen }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-x-3 top-[4.5rem] sm:absolute sm:inset-x-auto sm:top-full sm:right-0 sm:mt-1 sm:w-72 bg-white rounded-lg shadow-lg overflow-hidden z-50">
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <h3 className="font-medium text-gray-900">Resources</h3>
          <p className="text-sm text-gray-600">Helpful tools for your trademark journey</p>
        </div>
        <div className="p-2">
          {academyResources.map((item, index) => (
            <a
              key={index}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start p-3 rounded-md hover:bg-gray-50 transition-colors"
              onClick={() => setOpenMenu(null)}
            >
              <div className="flex-shrink-0 p-1">
                <item.icon className="w-5 h-5 text-[#00695c]" />
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-gray-900">{item.title}</h4>
                <p className="text-xs text-gray-600">{item.description}</p>
              </div>
            </a>
          ))}
        </div>
      </div>
    );
  };

  const NotificationsDropdown = ({ isOpen }) => {
    if (!isOpen) return null;

    const handleMarkAllAsRead = (e) => {
      e.stopPropagation();
      markAllAsReadMutation.mutate();
    };

    const handleNotificationClick = (notification) => {
      if (!notification.read) {
        markAsReadMutation.mutate(notification.id);
      }
      setOpenMenu(null);
    };

    return (
      <div className="fixed inset-x-3 top-[4.5rem] sm:absolute sm:inset-x-auto sm:top-full sm:right-0 sm:mt-1 sm:w-80 bg-white rounded-lg shadow-lg overflow-hidden z-50">
        <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h3 className="font-medium text-gray-900">Notifications</h3>
            <p className="text-sm text-gray-600">Your trademark activity updates</p>
          </div>
          {hasUnreadNotifications && (
            <button
              onClick={handleMarkAllAsRead}
              className="text-xs text-teal-600 hover:text-teal-800 font-medium"
              disabled={markAllAsReadMutation.isPending}
            >
              {markAllAsReadMutation.isPending ? 'Marking...' : 'Mark all as read'}
            </button>
          )}
        </div>
        <div className="overflow-y-auto max-h-80">
          {notifications.length > 0 ? (
            <div>
              {notifications.map(notification => {
                const notificationType = getNotificationType(notification);
                let Icon = CheckCircle;
                let iconColor = 'text-green-500';

                if (notificationType === 'warning') {
                  Icon = AlertCircle;
                  iconColor = 'text-amber-500';
                } else if (notificationType === 'info') {
                  Icon = Mail;
                  iconColor = 'text-blue-500';
                }

                return (
                  <div
                    key={notification.id}
                    className={`p-3 border-b border-gray-100 hover:bg-gray-50 flex items-start cursor-pointer ${!notification.read ? 'bg-teal-50' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className={`flex-shrink-0 ${iconColor}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="ml-3 flex-1">
                      <div className="flex justify-between items-start">
                        <h4 className={`text-sm font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                          {notification.title}
                        </h4>
                        <span className="text-xs text-gray-500">
                          {formatNotificationTime(notification.createdAt)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">{notification.message}</p>
                    </div>
                    {!notification.read && (
                      <div className="ml-2 flex-shrink-0">
                        <div className="h-2 w-2 rounded-full bg-teal-500"></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No notifications yet</p>
              <p className="text-xs mt-1">We'll notify you about important updates</p>
            </div>
          )}
        </div>
        <div className="p-3 border-t border-gray-200 bg-gray-50">
          <Link
            to="/notifications"
            className="block text-center text-sm text-teal-600 hover:text-teal-800 font-medium"
            onClick={() => setOpenMenu(null)}
          >
            View all notifications
          </Link>
        </div>
      </div>
    );
  };
  
  const SettingsDropdown = ({ isOpen }) => {
    if (!isOpen) return null;
    
    return (
      <div className="fixed inset-x-3 top-[4.5rem] sm:absolute sm:inset-x-auto sm:top-full sm:right-0 sm:mt-1 sm:w-72 bg-white rounded-lg shadow-lg overflow-hidden z-50">
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <h3 className="font-medium text-gray-900">Settings</h3>
          <p className="text-sm text-gray-600">Manage your account and preferences</p>
        </div>
        <div className="p-2">
          {settingsItems.map((item, index) => (
            <button
              key={index}
              className="w-full text-left flex items-start p-3 rounded-md hover:bg-gray-50 transition-colors"
              onClick={() => {
                item.action();
                setOpenMenu(null);
              }}
            >
              <div className="flex-shrink-0 p-1">
                <item.icon className="w-5 h-5 text-[#00695c]" />
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-gray-900">{item.title}</h4>
                <p className="text-xs text-gray-600">{item.description}</p>
              </div>
            </button>
          ))}
        </div>
        <div className="p-2 border-t border-gray-200">
          <button
            className="w-full text-left flex items-start p-3 rounded-md hover:bg-red-50 transition-colors"
            onClick={handleSignOut}
          >
            <div className="flex-shrink-0 p-1">
              <LogOut className="w-5 h-5 text-red-600" />
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-red-600">Log Out</h4>
              <p className="text-xs text-gray-600">Sign out of your account</p>
            </div>
          </button>
        </div>
      </div>
    );
  };

  return (
    <header className="bg-gradient-to-r from-[#004d40] via-[#00695c] to-[#00796b] shadow-md z-20 h-16 fixed top-0 w-full">
      <div className="px-4 h-full flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Mobile menu button — opens the sidebar drawer */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden text-white hover:bg-white/10 min-h-[44px] min-w-[44px]"
            onClick={onMenuClick}
            aria-label="Open navigation menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
          
          {/* App logo - MarkoTM branding (rendered white for the dark header) */}
          <Link to="/" className="flex items-center">
            <img
              src={markoLogo}
              alt="MarkoTM"
              className="h-9 w-auto brightness-0 invert"
            />
          </Link>
        </div>
        
        {/* Right section - User controls */}
        <div className="flex items-center gap-2">
          {/* Notifications dropdown */}
          <div className="relative">
            <button 
              className="p-2 rounded-full hover:bg-[#00897b] text-white relative"
              onClick={() => setOpenMenu(openMenu === 'notifications' ? null : 'notifications')}
            >
              <Bell className="h-5 w-5" />
              {hasUnreadNotifications && (
                <span className="absolute top-1 right-1 h-2.5 w-2.5 bg-red-500 rounded-full border border-[#00695c]"></span>
              )}
            </button>
            <NotificationsDropdown isOpen={openMenu === 'notifications'} />
          </div>
          
          {/* Help/Resources - simplified */}
          <div className="relative">
            <button 
              className="p-2 rounded-full hover:bg-white/10 text-white flex items-center transition-colors"
              onClick={() => setOpenMenu(openMenu === 'resources' ? null : 'resources')}
            >
              <HelpCircle className="h-5 w-5" />
            </button>
            <ResourceDropdown isOpen={openMenu === 'resources'} />
          </div>

          {/* Language Selector */}
          <LanguageSelector />

          {/* Admin Dashboard Button - Only visible to admins */}
          {hasAdminAccess && (
            <Link to="/admin">
              <button className="p-2 rounded-full bg-red-500/20 hover:bg-red-500/30 text-white flex items-center gap-1 transition-colors">
                <Shield className="h-4 w-4" />
              </button>
            </Link>
          )}
          
          {/* User profile - cleaner design */}
          {isSignedIn ? (
            <div className="flex items-center gap-2 ml-2 pl-2 border-l border-white/20">
              <button 
                className="flex items-center gap-2 p-1.5 rounded-full hover:bg-white/10 transition-colors"
                onClick={() => setOpenMenu(openMenu === 'settings' ? null : 'settings')}
              >
                <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center overflow-hidden ring-2 ring-white/30">
                  {user?.imageUrl ? (
                    <img src={user.imageUrl} alt="Profile" className="h-full w-full object-cover" />
                  ) : (
                    <User className="h-4 w-4 text-white" />
                  )}
                </div>
                <ChevronDown className={`h-4 w-4 text-white/70 transition-transform ${openMenu === 'settings' ? 'rotate-180' : ''}`} />
              </button>
              <SettingsDropdown isOpen={openMenu === 'settings'} />
            </div>
          ) : (
            <div className="flex items-center gap-2 ml-2 pl-2 border-l border-white/20">
              <span className="text-white/60 text-sm">Guest</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Backdrop for dropdown */}
      {openMenu && (
        <div 
          className="fixed inset-0 bg-black/10 backdrop-blur-sm z-10"
          onClick={() => setOpenMenu(null)}
        />
      )}
    </header>
  );
};

export default DashboardHeader;
